import isEqual from "lodash/isEqual";
import { getActiveUser } from "@noom/hive-auth";
import {
  addSurveyAnswers,
  SurveyAnswersState,
} from "@utils/redux/slices/surveyAnswers";
import { updateCurrentAnswers } from "@utils/redux/slices/currentAnswers";
import { getWeightLossGoalData } from "@utils/pace";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { SessionStorageKey } from "src/utils/constants";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { getUserDetails, getUserProfile } from "src/utils/api/userDetails";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { saveSurveyAnswers } from "src/utils/redux/slices/userData";
import { transformUserDetailsToSurveyAnswers } from "src/utils/userDetailsTransform";
import { ActionHandler, ActionHandlerParams } from "..";

/**
 * For an authenticated user, coach server is the source of truth for the basic
 * profile details, so its values win over the answers we already have. It only
 * describes what it actually knows, leaving the rest of the survey untouched.
 *
 * This matters most for the upsell surveys, which ask for the goal weight
 * before the current weight and never ask for height. When the userdata blob
 * carries no earlier weight either, /survey/idealWeight is left with nothing to
 * validate against.
 *
 * The answers travel back to coach server over several paths rather than one:
 * some as soon as they are answered, the rest at purchase and through
 * publishBuyflowSurveyAnswersInUserModel.
 */
export const prefillSurveyAnswersFromCoachServer: ActionHandler = async ({
  store,
}: ActionHandlerParams) => {
  const accessCode = getActiveUser()?.accessCode;
  if (!accessCode) {
    return;
  }
  const sessionKey = SessionStorageKey.prefilledSurveyAnswersFromCoachServer;

  if (getNoomSessionStorage<boolean>(sessionKey)) {
    return;
  }

  const task = trackTask("PrefillSurveyAnswersFromCoachServer");
  task.start();

  try {
    // The name lives on a separate endpoint from the measurements.
    const [userDetails, userProfile] = await Promise.all([
      getUserDetails(accessCode),
      getUserProfile(accessCode),
    ]);

    const answers: Partial<SurveyAnswersState> = userDetails
      ? transformUserDetailsToSurveyAnswers(userDetails)
      : {};

    if (userProfile?.name) {
      answers.name = userProfile.name;
    }

    // Reporting the keys rather than the answers keeps PII out of the event.
    // How often coach server disagrees with the survey is worth watching, since
    // every disagreement is an answer we replace under the user.
    const knownAnswers = getSurveyAnswers();
    const syncedKeys = Object.keys(answers);
    const replacedKeys = syncedKeys.filter(
      (key) =>
        knownAnswers[key] != null && !isEqual(knownAnswers[key], answers[key])
    );

    if (!syncedKeys.length) {
      task.success({ syncedKeys, replacedKeys });
      setNoomSessionStorage(sessionKey, true);
      return;
    }

    store.dispatch(addSurveyAnswers(answers));

    // The action runs ahead of the survey block, so there is no survey to read
    // the namespace off of. Both page sets that call it open the main survey.
    store.dispatch(updateCurrentAnswers({ namespace: "mainSurvey", answers }));

    // Every save derives the top level weight and height from the answers it can
    // already see, which excludes the ones this save is about to introduce. Coach
    // server describes the user well enough to derive them here instead, so app
    // and CS read the same measurements without waiting for the next save.
    await store.dispatch(
      saveSurveyAnswers(answers, {
        surveyNameSpace: store.getState().surveyNameSpace,
        overwrite: false,
        extraProps: getWeightLossGoalData({ ...knownAnswers, ...answers }),
      })
    );

    task.success({ syncedKeys, replacedKeys });
    setNoomSessionStorage(sessionKey, true);
  } catch (err) {
    task.failed(err);
  }
};
